import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import gridStyle from "../ClinicalResources.module.scss";
import styles from "./ORStretcherPads.module.scss";
import { OR_AND_STRECHER_LIST, VIEW } from "../constant";
import { createTableOrStrtcherPads } from "../../../../../app/homeSlice";
import { ResourceCard } from "../ResourceCard";
import { TablePadSelection } from "./TablePadSelection";
import { TablePadFinalView } from "./TablePadFinalView";
import { StretcherPadView } from "./StretcherPadView";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";
import { OPEN_FILE_DOWNLOAD_URL } from "../../../../../constants/apiBaseConfig";

const TABLE_PAD_FORM_DEFAULT = { padHeight: "", style: "", coverOptions: "" };
const STRETCHER_FORM_DEFAULT = { width: "", length: "", style: "", thickness: "", corner: "" };

export const ORStretcherPads = ({ view, setView }) => {
  const dispatch = useDispatch();
  const customerNumber = useSelector((s) => s.user.customerNumber);

  const submitLoading = useLoading(LOADING_KEYS.CREATE_TABLE_PADS);

  const [selectedTablePad, setSelectedTablePad] = useState(null);
  const [tablePadForm, setTablePadForm] = useState(TABLE_PAD_FORM_DEFAULT);
  const [tablePadErrors, setTablePadErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [stretcherForm, setStretcherForm] = useState(STRETCHER_FORM_DEFAULT);
  const [stretcherErrors, setStretcherErrors] = useState({});

  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(() => {
      setSubmitted(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitted]);

  const handleSubResourceSelect = useCallback(
    (key) => {
      if (key === "tablePads") setView(VIEW.TABLE_PAD_VIEW);
      if (key === "stretcherPads") setView(VIEW.STRETCHER_PAD_VIEW);
    },
    [setView]
  );

  const handleTablePadNext = useCallback(() => {
    if (!selectedTablePad) return;
    setView(VIEW.TABLE_PAD_FINAL_VIEW);
  }, [selectedTablePad, setView]);

  const handleTablePadSubmit = useCallback(async () => {
    const errors = {};
    if (!tablePadForm.padHeight) errors.padHeight = "Required";
    if (!tablePadForm.style) errors.style = "Required";
    if (!tablePadForm.coverOptions) errors.coverOptions = "Required";
    setTablePadErrors(errors);
    if (Object.keys(errors).length) return;

    const payload = {
      customername: "",
      customernumber: customerNumber,
      title: "Table Pads",
      tablePads: {
        modelName: selectedTablePad.modelName,
        options: tablePadForm.coverOptions,
        padHeight: tablePadForm.padHeight,
        style: tablePadForm.style,
      },
    };
    const result = await dispatch(createTableOrStrtcherPads(payload));
    if (result) {
      setSubmitted(true);
    }
  }, [dispatch, customerNumber, selectedTablePad, tablePadForm]);

  const handleStretcherSubmit = useCallback(async () => {
    const errors = {};
    ["width", "length", "style", "thickness", "corner"].forEach((f) => {
      if (!stretcherForm[f]) errors[f] = "Required";
    });
    setStretcherErrors(errors);
    if (Object.keys(errors).length) return;

    const payload = {
      customername: "",
      customernumber: customerNumber,
      title: "Table Pads",
      stretcherPads: {
        corner: stretcherForm.corner,
        length: stretcherForm.length,
        style: stretcherForm.style,
        thickness: stretcherForm.thickness,
        width: stretcherForm.width,
      },
    };
    const result = await dispatch(createTableOrStrtcherPads(payload));
    if (result) {
      setSubmitted(true);
    }
  }, [dispatch, customerNumber, stretcherForm]);

  const handleDownloadCustomForm = useCallback(() => {
    const fileName = "Custom_OR_And_Stretcher_Order_Form.pdf";
    const downloadUrl = `${OPEN_FILE_DOWNLOAD_URL}/documents/${fileName}`;

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return (
    <div className={styles.container}>
      {view === VIEW.OR_STRETCHER_VIEW && (
        <div className={gridStyle.resourceGrid}>
          {OR_AND_STRECHER_LIST.map((resource) => (
            <ResourceCard key={resource.key} resource={resource} onClick={handleSubResourceSelect} />
          ))}
        </div>
      )}

      {view === VIEW.TABLE_PAD_VIEW && <TablePadSelection selected={selectedTablePad} onSelect={setSelectedTablePad} onNext={handleTablePadNext} onDownload={handleDownloadCustomForm} />}

      {view === VIEW.TABLE_PAD_FINAL_VIEW && selectedTablePad && (
        <TablePadFinalView
          selectedTablePad={selectedTablePad}
          formData={tablePadForm}
          setFormData={setTablePadForm}
          errors={tablePadErrors}
          submitting={submitLoading}
          submitted={submitted}
          onSubmit={handleTablePadSubmit}
        />
      )}

      {view === VIEW.STRETCHER_PAD_VIEW && (
        <StretcherPadView
          formData={stretcherForm}
          setFormData={setStretcherForm}
          errors={stretcherErrors}
          submitting={submitLoading}
          submitted={submitted}
          onSubmit={handleStretcherSubmit}
          onDownload={handleDownloadCustomForm}
        />
      )}
    </div>
  );
};
